import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

/**
 * Person names: letters (incl. common accents), optional spaces / hyphens / apostrophes.
 * Digits and other symbols are rejected. Minimum 2 letters (single char is not a name).
 */
export const PERSON_NAME_MIN_LENGTH = 2;

export const PERSON_NAME_PATTERN =
  /^[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[ '\-][A-Za-zÀ-ÖØ-öø-ÿ]+)*$/;

export const PERSON_NAME_INVALID_MESSAGE =
  'Enter a valid name (at least 2 letters, no numbers).';

/** Blocks digit keypresses while allowing navigation / editing keys. */
export function isPersonNameDigitKey(event: KeyboardEvent): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return false;
  }
  return event.key.length === 1 && /\d/.test(event.key);
}

/** Strip digits (and normalize) from pasted/typed name input. */
export function sanitizePersonNameInput(raw: unknown): string {
  if (raw == null) {
    return '';
  }
  return String(raw).replace(/\d+/g, '');
}

export function personNameValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (raw == null || String(raw).trim() === '') {
    return null;
  }

  const value = String(raw).trim();
  const letterCount = (value.match(/[A-Za-zÀ-ÖØ-öø-ÿ]/g) ?? []).length;

  if (
    /\d/.test(value) ||
    letterCount < PERSON_NAME_MIN_LENGTH ||
    !PERSON_NAME_PATTERN.test(value)
  ) {
    return { personName: true };
  }
  return null;
}

export interface PersonNameValidatorOptions {
  required?: boolean;
  maxLength?: number;
  minLength?: number;
}

export function getPersonNameValidators(
  options: PersonNameValidatorOptions = {}
): ValidatorFn[] {
  const validators: ValidatorFn[] = [personNameValidator];

  if (options.required) {
    validators.unshift(Validators.required);
  }
  if (options.minLength != null) {
    validators.push(Validators.minLength(options.minLength));
  }
  if (options.maxLength != null) {
    validators.push(Validators.maxLength(options.maxLength));
  }

  return validators;
}
